import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

// Auxiliares para White Label
const storagePrefix = process.env.REACT_APP_STORAGE_PREFIX || 'app';
const getK = (key) => `${storagePrefix}_${key}`;
const IS_DEMO = process.env.REACT_APP_IS_DEMO === 'true';

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    if (IS_DEMO) return 'token_demo_123';
    return localStorage.getItem(getK('token'));
  });
  const [refreshToken, setRefreshToken] = useState(() => {
    if (IS_DEMO) return 'refresh_token_demo_123';
    return localStorage.getItem(getK('refresh_token'));
  });
  const [user, setUser] = useState(() => {
    if (IS_DEMO) {
      return {
        id: 999,
        nome: "ADMINISTRADOR DEMO",
        cargo: "ADMIN",
        acesso: 'admin',
        senha_temporaria: false
      };
    }
    return JSON.parse(localStorage.getItem(getK('user'))) || null;
  });
  const [acesso, setAcesso] = useState(() => {
    if (IS_DEMO) return 'admin';
    return localStorage.getItem(getK('acesso')) || 'colaborador';
  });

  // ✅ Função para verificar expiração (Bypass se for Demo)
  const isTokenExpired = useCallback((tkn) => {
    if (IS_DEMO) return false;
    try {
      const decoded = jwtDecode(tkn);
      return decoded.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }, []);

  // ✅ Estabilizando o logout para ser usado em outras funções
  const logout = useCallback(() => {
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setAcesso('colaborador');
    localStorage.removeItem(getK('token'));
    localStorage.removeItem(getK('refresh_token'));
    localStorage.removeItem(getK('user'));
    localStorage.removeItem(getK('acesso'));
  }, []);

  // ✅ Estabilizando a atualização de usuário
  const updateUserFromToken = useCallback((tkn) => {
    if (IS_DEMO && tkn === 'token_demo_123') {
      const demoUser = {
        id: 999,
        nome: "ADMINISTRADOR DEMO",
        cargo: "ADMIN",
        acesso: 'admin',
        senha_temporaria: false
      };
      setAcesso('admin');
      setUser(demoUser);
      localStorage.setItem(getK('acesso'), 'admin');
      localStorage.setItem(getK('user'), JSON.stringify(demoUser));
      return;
    }

    try {
      const decoded = jwtDecode(tkn);
      const acessoVal = decoded.acesso || 'colaborador';
      const userData = {
        id: decoded.sub ? parseInt(decoded.sub, 10) : null,
        nome: decoded.nome,
        cargo: decoded.cargo,
        acesso: acessoVal,
        senha_temporaria: decoded.senha_temporaria
      };
      setAcesso(acessoVal);
      setUser(userData);
      localStorage.setItem(getK('acesso'), acessoVal);
      localStorage.setItem(getK('user'), JSON.stringify(userData));
    } catch {
      logout();
    }
  }, [logout]);

  // ✅ Estabilizando a renovação de token
  const renovarToken = useCallback(async () => {
    if (IS_DEMO || !refreshToken) return;
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { Authorization: `Bearer ${refreshToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setToken(data.token);
        localStorage.setItem(getK('token'), data.token);
        updateUserFromToken(data.token);
      } else {
        logout();
      }
    } catch {
      logout();
    }
  }, [refreshToken, logout, updateUserFromToken]);

  // ✅ Efeito de Inicialização (Corrigido o warning de dependência)
  useEffect(() => {
    if (token) {
      updateUserFromToken(token);
    }
  }, [token, updateUserFromToken]);

  // ✅ Efeito de Refresh (Corrigido o warning de dependência)
  useEffect(() => {
    const interval = setInterval(() => {
      if (!IS_DEMO && token && isTokenExpired(token)) {
        renovarToken();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [token, isTokenExpired, renovarToken]);

  const login = useCallback((newToken, newRefreshToken) => {
    const finalToken = IS_DEMO ? 'token_demo_123' : newToken;
    setToken(finalToken);
    setRefreshToken(newRefreshToken);
    localStorage.setItem(getK('token'), finalToken);
    localStorage.setItem(getK('refresh_token'), newRefreshToken);
    updateUserFromToken(finalToken);
  }, [updateUserFromToken]);

  return (
    // ✅ Adicionado 'isDemo' no Provider para que as páginas consigam ler a flag
    <AuthContext.Provider value={{ 
      token, 
      refreshToken, 
      acesso, 
      user, 
      login, 
      logout, 
      isDemo: IS_DEMO 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);